import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { Grid, InputAdornment, TextField } from '@mui/material';
import dayjs from 'dayjs';
import type { MRT_ColumnDef } from 'material-react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ProductUbicacionSeriesModal } from '@/erp/tecnico/orden-trabajo/shared/components/SaveOrdenTrabajo/equipos';
import {
  EstadosRubroChoiceEnum,
  TipoRubroChoiceEnum,
  generateUUID,
} from '@/shared';
import {
  CreateRubroLibreClienteData,
  getUbicacionProductos,
  useCreateRubro,
  useUpdateRubro,
} from '@/shared/actions/erp';
import {
  CustomSelectTextFieldNoForm,
  SampleTable,
  SingleButton,
  SingleIconButton,
} from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import {
  AlertSeverityEnum,
  Contrato,
  IDataProducto,
  IVA,
  RubroProductDetail,
} from '@/shared/interfaces';
import { MaterialesKey, useMaterialesUtilizadosStore } from '@/store/erp';
import { useRecaudacionesStore } from '@/store/erp/recaudaciones';
import { useIvaStore } from '@/store/erp/shared/iva.store';
import { useUiConfirmModalStore, useUiStore } from '@/store/ui';
import {
  ClienteRubroLibreTableType,
  useColumnsRubrosCliente,
} from '../../../hooks';
import { ClienteRubroLibrePopover, TotalAmountRubloLireCard } from './libre';

export type ClienteRuboLibreModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  contract: Contrato;
};

export type RubrosClienteFormDataType = {
  categoria_producto: string;
  producto: string;

  fecha_vencimiento: string;
  detalle?: RubroProductDetail; // JSON

  iva_producto: string; // porcentaje

  // fk
  bodega: string;
  ubicacion: string;
  tipo_rubro: TipoRubroChoiceEnum;
};

const ClienteRuboLibreModal: React.FC<ClienteRuboLibreModalProps> = ({
  contract,
  open,
  setOpen,
}) => {
  /////* hooks ==================================

  /////* local state ==================================
  const [openSeriesModal, setOpenSeriesModal] = useState(false);

  ////* global state ==================================
  const currentRubro = useRecaudacionesStore(s => s.currentRubro);
  const selectedItems: ClienteRubroLibreTableType[] =
    useMaterialesUtilizadosStore(s => s.selectedItems);
  const setActiveItemRow = useMaterialesUtilizadosStore(
    s => s.setActiveItemRow
  );
  const setCurrentRubro = useRecaudacionesStore(s => s.setCurrentRubro);

  const ivas = useIvaStore(s => s.ivas);

  const updateSelectedItemQuantity = useMaterialesUtilizadosStore(
    s => s.updateSelectedItemQuantity
  );
  const removeSelectedItem = useMaterialesUtilizadosStore(
    s => s.removeSelectedItem
  );
  const clearSelectedItems = useMaterialesUtilizadosStore(
    s => s.clearSelectedItems
  );
  const setSelectedItems = useMaterialesUtilizadosStore(
    s => s.setSelectedItems
  );

  const setAlert = useUiStore(s => s.setAlert);
  const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* form ==================================
  const form = useForm<RubrosClienteFormDataType>({
    defaultValues: {
      fecha_vencimiento: dayjs().format('YYYY-MM-DD'),
      tipo_rubro: TipoRubroChoiceEnum.LIBRE,
    },
  });

  ////* mutations ==================================
  const createRubro = useCreateRubro<CreateRubroLibreClienteData>({
    enableRedirect: false,
    customSuccessMessage: 'Rubro Libre creado con éxito',
  });
  const updRubro = useUpdateRubro<CreateRubroLibreClienteData>({
    enableRedirect: false,
    customSuccessMessage: `Rubro Libre '${currentRubro?.numero_referencia}' actualizado con éxito`,
  });

  ////* handlers ==================================
  const handleClose = () => {
    setOpen(false);
    form.reset();
  };

  // // table operations ---------------
  const calcTotalLine = (item: ClienteRubroLibreTableType): string => {
    return (
      (
        +(item.precio_1 || 0) *
        +(item.cantidadUtilizada || 0) *
        (+(item.ivaValue || 0) / 100 + 1)
      ).toFixed(2) || ''
    );
  };

  const onChangePrice = useCallback(
    (value: string, item: ClienteRubroLibreTableType) => {
      updateSelectedItemQuantity(
        {
          ...item,
          precio_1: value,

          total: calcTotalLine({
            ...item,
            precio_1: +value,
          }),
        },
        MaterialesKey.selectedItems
      );
    },
    [updateSelectedItemQuantity]
  );

  const onChangeQuantity = useCallback(
    (value: number, item: ClienteRubroLibreTableType) => {
      const stockMaximo = +item.stock;
      const isNewLine = !!item?.isNewLine;

      if (!isNewLine && +value > stockMaximo) {
        return setAlert(
          true,
          `La cantidad máxima permitida es ${stockMaximo}`,
          AlertSeverityEnum.error
        );
      }

      updateSelectedItemQuantity(
        {
          ...item,
          cantidadUtilizada: +value,

          // reset series when quantity is changed
          selectedSeries: [],
          savedSeries: [],

          total: calcTotalLine({
            ...item,
            cantidadUtilizada: +value,
          }),
        },
        MaterialesKey.selectedItems
      );
    },
    [updateSelectedItemQuantity]
  );

  const onChangeImpuesto = useCallback(
    (iva: IVA, item: ClienteRubroLibreTableType) => {
      updateSelectedItemQuantity(
        {
          ...item,
          ivaCode: iva.codigo,
          ivaValue: +(iva?.porcentaje || 0) || 0,

          total: calcTotalLine({
            ...item,
            ivaCode: iva.codigo,
            ivaValue: +(iva?.porcentaje || 0) || 0,
          }),
        },
        MaterialesKey.selectedItems
      );
    },
    [updateSelectedItemQuantity]
  );

  const onChangeSeries = useCallback(
    (value: string[], item: ClienteRubroLibreTableType) => {
      updateSelectedItemQuantity(
        { ...item, seriesAll: value },
        MaterialesKey.selectedItems,
        'id'
      );
    },
    [updateSelectedItemQuantity]
  );

  const onChangeGenericProductDataText = useCallback(
    (
      value: string,
      item: ClienteRubroLibreTableType,
      key: keyof IDataProducto
    ) => {
      updateSelectedItemQuantity(
        {
          ...item,
          producto_data: {
            ...item.producto_data,
            [key]: value,
          },
        },
        MaterialesKey.selectedItems
      );
    },
    [updateSelectedItemQuantity]
  );

  const onChangeGenericFirstLevel = useCallback(
    (
      value: string | number | boolean,
      item: ClienteRubroLibreTableType,
      key: keyof ClienteRubroLibreTableType
    ) => {
      updateSelectedItemQuantity(
        {
          ...item,
          [key]: value,
        },
        MaterialesKey.selectedItems
      );
    },
    [updateSelectedItemQuantity]
  );

  // summary operations ---------------
  const calcTotal = (): string => {
    return selectedItems
      .reduce(
        (acc, item) =>
          acc +
          +(item.precio_1 || 0) *
            +(item.cantidadUtilizada || 0) *
            (+(item.ivaValue || 0) / 100 + 1),
        0
      )
      .toFixed(2);
  };

  const calcSubTotal = (): string => {
    return selectedItems
      .reduce(
        (acc, item) =>
          acc + (item.precio_1 || 0) * (item.cantidadUtilizada || 0),
        0
      )
      .toFixed(2);
  };

  const calcTotalTaxes = (): string => {
    return selectedItems
      .reduce(
        (acc, item) =>
          acc +
          +(item.precio_1 || 0) *
            +(item.cantidadUtilizada || 0) *
            (+(item.ivaValue || 0) / 100),
        0
      )
      .toFixed(2);
  };

  const handleComplete = () => {
    clearSelectedItems(MaterialesKey.selectedItems);
    handleClose();

    setIsGlobalLoading(false);
  };

  // onSave ------
  const onSave = async (data: RubrosClienteFormDataType) => {
    // // validations =======================
    const selectedItems: ClienteRubroLibreTableType[] =
      useMaterialesUtilizadosStore.getState().selectedItems;

    if (!selectedItems.length) {
      setAlert(
        true,
        'No se puede crear un rubro sin items',
        AlertSeverityEnum.error
      );
      return;
    }

    // validate series --------------
    let thereAreItemsWithoutSeries = false;
    let thereAreItemsWithZeroQuantity = false;
    selectedItems.forEach(item => {
      if (item.contieneSeries && !item.savedSeries?.length) {
        thereAreItemsWithoutSeries = true;
      }

      if (!item.cantidadUtilizada) {
        thereAreItemsWithZeroQuantity = true;
      }
    });

    if (thereAreItemsWithZeroQuantity) {
      setAlert(true, 'Hay items sin cantidad', AlertSeverityEnum.error);
      return;
    }
    if (thereAreItemsWithoutSeries) {
      setAlert(true, 'Hay items sin series asignadas', AlertSeverityEnum.error);
      return;
    }

    // isNewLine validation --------------
    let thereAreEmpyCodes = false;
    let thereAreEmpyDescriptions = false;
    selectedItems.forEach(item => {
      if (!item?.producto_data?.codigo) {
        thereAreEmpyCodes = true;
      }

      if (!item?.producto_data?.descripcion) {
        thereAreEmpyDescriptions = true;
      }
    });
    if (thereAreEmpyCodes || thereAreEmpyDescriptions) {
      setAlert(
        true,
        'Hay items sin código o descripción',
        AlertSeverityEnum.error
      );
      return;
    }

    ///* update -------------------------
    if (!!currentRubro?.id_rubro) {
      setIsGlobalLoading(true);
      await updRubro.mutateAsync({
        id: currentRubro?.id_rubro!,
        data: {
          valor_total: calcTotal(),
          valor_iva: calcTotalTaxes(),
          valor_base: calcSubTotal(),
          tipo_rubro: data.tipo_rubro,
          cliente: contract?.cliente!,
          detalle: selectedItems.map(item => ({
            codigo: item.codigo!,
            cantidad: item.cantidadUtilizada?.toString() || '0.00',
            series: item.savedSeries!,

            producto_data: item.producto_data!,

            ivaValue: item.ivaValue,
            ivaCode: item.ivaCode,

            isNewLine: !!item?.isNewLine,
            precio_1: (+(item?.precio_1 || 0))?.toFixed(2) || '0.00',
            totalLine: calcTotalLine(item),

            bodega: item?.bodega || null,
            ubicacion: item?.ubicacion || null,
          })),

          fecha_vencimiento: data.fecha_vencimiento,
        } as unknown as CreateRubroLibreClienteData,
      });
      handleComplete();

      return;
    }

    ///* create -------------------------
    setIsGlobalLoading(true);
    await createRubro.mutateAsync({
      valor_total: calcTotal(),
      valor_iva: calcTotalTaxes(),
      valor_base: calcSubTotal(),
      tipo_rubro: data.tipo_rubro,
      cliente: contract?.cliente!,
      detalle: selectedItems.map(item => ({
        codigo: item.codigo!,
        cantidad: item.cantidadUtilizada?.toString() || '0.00',
        series: item.savedSeries!,

        producto_data: item.producto_data!,

        ivaValue: item.ivaValue,
        ivaCode: item.ivaCode,

        isNewLine: !!item?.isNewLine,
        precio_1: (+(item?.precio_1 || 0))?.toFixed(2) || '0.00',
        totalLine: calcTotalLine(item),

        bodega: item?.bodega || null,
        ubicacion: item?.ubicacion || null,
      })),

      fecha_emision: dayjs().format(),
      fecha_vencimiento: data.fecha_vencimiento,
      contrato: contract?.codigo!,
    });

    handleComplete();
  };

  ///* colums ==================================
  const { columnsClienteRubrosLibreb01 } = useColumnsRubrosCliente({
    contract,
    onChangeCode: (e, currentItem) => {
      const value = e?.target?.value?.toUpperCase();

      onChangeGenericProductDataText(value, currentItem!, 'codigo');
    },
    onChangeDescription: (e, currentItem) => {
      const value = e?.target?.value?.toUpperCase();

      onChangeGenericProductDataText(value, currentItem!, 'descripcion');
    },
  });

  const columnsClienteRubrosLibre = useMemo<
    MRT_ColumnDef<ClienteRubroLibreTableType>[]
  >(
    () => [
      ...columnsClienteRubrosLibreb01,

      {
        accessorKey: 'precio_1',
        header: 'Precio',
        size: 120,
        Cell: ({ row }) => {
          return (
            <TextField
              variant="outlined"
              value={row.original.precio_1}
              onChange={e => {
                const regex = /^\d*\.?\d{0,2}$/;
                let value = e.target.value;

                if (regex.test(value)) {
                  onChangePrice(value, row.original);
                }
              }}
              type="number"
              inputProps={{ step: 0.01 }}
            />
          );
        },
      },

      {
        accessorKey: 'impuesto',
        header: 'Imp %',
        size: 100,
        Cell: ({ row }) => {
          return (
            <>
              <CustomSelectTextFieldNoForm<IVA>
                options={ivas || []}
                menuItemKey="codigo"
                valueKey="codigo"
                labelKey="porcentaje"
                defaultValue={row.original.ivaCode || ''}
                onChangeValue={ivaCode => {
                  const iva = ivas.find(iva => iva?.codigo === ivaCode);
                  if (!iva) return;

                  onChangeImpuesto(iva, row.original);
                }}
              />
            </>
          );
        },
      },

      {
        accessorKey: 'cantidad',
        header: 'Cantidad',
        size: 180,
        Cell: ({ row }) => {
          const isEditting =
            !!useRecaudacionesStore.getState().currentRubro?.id_rubro;

          // upd rubro ------
          if (isEditting) {
            const canUpdateRubro = row.original?.canUpdate || false;

            return (
              <Grid item container xs={12} spacing={1} alignItems="center">
                <Grid item xs={10}>
                  <TextField
                    variant="outlined"
                    value={row.original.cantidadUtilizada || ''}
                    onChange={e => {
                      const intValue = Math.floor(parseInt(e.target.value, 10));

                      onChangeQuantity(intValue, {
                        ...row.original,
                        id: row.original.id,
                      });
                    }}
                    type="number"
                    inputProps={{ min: 1 }}
                    disabled={!canUpdateRubro}
                  />
                </Grid>
                <Grid item xs={1}>
                  <SingleIconButton
                    startIcon={<EditIcon />}
                    label="Editar"
                    color="inherit"
                    onClick={() => {
                      setConfirmDialog({
                        isOpen: true,
                        title: 'Editar Cantidad',
                        subtitle:
                          '¿Está seguro que desea editar la cantidad del item? Una vez actualizada deberá seleccionar nuevamente las series.',
                        onConfirm: async () => {
                          setConfirmDialogIsOpen(false);

                          // fetch data and upd row ----------
                          setIsGlobalLoading(true);
                          const { data } = await getUbicacionProductos({
                            ubicacion: row.original?.ubicacion!,
                            producto: row.original?.producto_data?.codigo!,
                          });
                          setIsGlobalLoading(false);

                          const currentItemCode =
                            row.original?.producto_data?.codigo;
                          const currentStock = data?.data?.at(0)?.stock || 0;

                          const selectedItems =
                            useMaterialesUtilizadosStore.getState()
                              .selectedItems;

                          // find item to update
                          const currentItem = selectedItems.find(
                            item =>
                              item?.producto_data?.codigo === currentItemCode
                          );

                          // update item
                          onChangeGenericFirstLevel(
                            true,
                            {
                              ...currentItem,
                              stock: currentStock,
                            },
                            'canUpdate'
                          );

                          // to force re-render ----------
                          // onChangeGenericFirstLevel(
                          //   true,
                          //   row.original,
                          //   'canUpdate'
                          // );
                        },
                      });
                    }}
                    disabled={canUpdateRubro}
                  />
                </Grid>
              </Grid>
            );
          }

          return (
            <TextField
              variant="outlined"
              value={row.original.cantidadUtilizada || ''}
              onChange={e => {
                const intValue = Math.floor(parseInt(e.target.value, 10));

                onChangeQuantity(intValue, {
                  ...row.original,
                  id: row.original.id,
                });
              }}
              type="number"
              inputProps={{ min: 1 }}
            />
          );
        },
      },

      {
        accessorKey: 'total',
        header: 'Total',
        size: 200,
        Cell: ({ row }) => {
          return (
            <TextField
              variant="outlined"
              value={calcTotalLine(row.original)}
              type="number"
              inputProps={{ min: 1 }}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          );
        },
      },

      {
        accessorKey: 'selected_series',
        header: 'Series Seleccionadas',
        Cell: ({ row }) => {
          return !row.original?.contieneSeries
            ? 'SIN SERIES'
            : row.original?.savedSeries?.length
            ? row.original?.savedSeries?.length
            : 0;
        },
      },
      {
        accessorKey: 'series',
        header: 'Series Disponibles',
        Cell: ({ row }) => {
          const hasSeries = row.original?.contieneSeries;

          return hasSeries ? (
            <>
              <SingleIconButton
                startIcon={<QrCode2Icon />}
                color="info"
                label="Ver Series"
                onClick={async () => {
                  // direct access to avoid reference issues
                  const currentRubro =
                    useRecaudacionesStore.getState().currentRubro;

                  // editting item ------
                  if (!!currentRubro?.id_rubro) {
                    setIsGlobalLoading(true);
                    const { data } = await getUbicacionProductos({
                      ubicacion: row.original?.ubicacion!,
                      producto: row.original?.producto_data?.codigo!,
                    });

                    setOpenSeriesModal(true);
                    const allSeries: string[] = data?.data?.at(0)?.series || [];
                    setActiveItemRow({
                      ...row.original,
                      allSeries,
                    });

                    setIsGlobalLoading(false);

                    return;
                  }

                  // new item - normal way ------
                  setOpenSeriesModal(true);
                  const allSeries: string[] = row.original?.series || [];
                  setActiveItemRow({
                    ...row.original,
                    allSeries,
                  });

                  // set series to product in table
                  onChangeSeries(allSeries, row.original);
                }}
              />
            </>
          ) : (
            <>SIN SERIES</>
          );
        },
      },

      {
        accessorKey: 'remove',
        header: 'Eliminar',
        size: 60,
        Cell: ({ row }) => {
          return (
            <SingleButton
              newCustomButton
              iconButton
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                removeSelectedItem(
                  row.original?.id!, // id actually exist in stored data
                  MaterialesKey.selectedItems
                );
              }}
            />
          );
        },
      },
    ],
    []
  );

  ///* effects ==================================
  // upd rubro ------------
  useEffect(() => {
    if (!currentRubro?.id_rubro || !open) return;

    if (currentRubro.estado_rubro === EstadosRubroChoiceEnum.PAGO_INCOMPLETO) {
      setAlert(
        true,
        'No se puede editar un rubro con transacciones.',
        AlertSeverityEnum.error
      );
      return setOpen(false);
    }

    const { detalle } = currentRubro;
    form.reset({
      ...form.getValues(),
      fecha_vencimiento: dayjs(currentRubro.fecha_vencimiento).format(
        'YYYY-MM-DD'
      ),
    });

    // ClienteRubroLibreTableType
    setSelectedItems(
      detalle?.map(item => {
        const uuid = generateUUID();
        const isNewLine = item?.isNewLine;

        return {
          ...item,
          id: isNewLine ? uuid : item?.producto_data?.id_producto,
          cantidadUtilizada: +item.cantidad,
          contieneSeries: !!item?.series?.length,
          savedSeries: item?.series || [],

          series: [], // all series at the moment, including selected ones
          isUpdating: true,
          canUpdate: false,
        };
      }) || [],
      MaterialesKey.selectedItems
    );

    return () => {
      clearSelectedItems(MaterialesKey.selectedItems);
      setActiveItemRow(null);
      setCurrentRubro(null);
      form.reset();
    };
  }, [currentRubro, open]);

  return (
    <>
      <ScrollableDialogProps
        open={open}
        title={`${
          currentRubro?.id_rubro ? 'Editar Rubro Libre' : 'Crear Rubro Libre'
        }`}
        minWidth="75%"
        onClose={handleClose}
        // // confirm --------
        confirmVariantBtn="outlined"
        confirmTextBtn="Guardar"
        onConfirm={form.handleSubmit(onSave, err => {
          if (err?.fecha_vencimiento?.message)
            return setAlert(
              true,
              'La fecha de vencimiento es requerida',
              AlertSeverityEnum.error
            );

          setAlert(true, 'Faltan campos requeridos', AlertSeverityEnum.error);
        })}
        // // content --------
        contentNode={
          <Grid container spacing={3} mt={2} mb={1}>
            {/* ================== button popover ================== */}
            <ClienteRubroLibrePopover contract={contract} form={form} />

            {/* ================== items table ================== */}
            <Grid item xs={12}>
              <SampleTable<ClienteRubroLibreTableType>
                columns={columnsClienteRubrosLibre}
                data={selectedItems}
              />
            </Grid>

            {/* ================== total ================== */}
            <TotalAmountRubloLireCard
              calcSubTotal={calcSubTotal}
              calcTotalTaxes={calcTotalTaxes}
              calcTotal={calcTotal}
            />
          </Grid>
        }
      />

      {/* ------ Product Series Modal ------ */}
      <ProductUbicacionSeriesModal
        open={openSeriesModal}
        setOpen={setOpenSeriesModal}
        onChageKeyArrayStore={MaterialesKey.selectedItems}
      />
    </>
  );
};

export default ClienteRuboLibreModal;
